import { useState } from 'react';
import { useStore } from '../../../../../store/mst';
import { NewPatchRouteDto } from '../../../../../store/mst/models/RootStore.base';
import getAppEnv from '../../../../../store/mst/utils/getAppEnv';
import { moveRouteFailureNotification } from '../../../../../notifications/routes/moveRoute';

interface UseMoveRoutesReturn {
  isSubmitting: boolean;
  moveRoutes: (routeUuids: string[], targetDate: string) => Promise<void>;
}

const useMoveRoutes = (): UseMoveRoutesReturn => {
  const store = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedWorkspaceId = store.ui.selectedWorkspace?.id;

  const moveRoutes = async (routeUuids: string[], targetDate: string): Promise<void> => {
    if (!selectedWorkspaceId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    // Record the move intent BEFORE firing the mutation. The terminal UpdateRouteConstraints status
    // arrives over a separate subscription/websocket channel, so it can be delivered before the
    // mutation's HTTP response resolves; setting the intent up front guarantees the handler can claim
    // that event instead of dropping the move toast. The action UUID isn't known yet — we reconcile
    // it below so the handler can correlate precisely once it is.
    store.ui.setIsMovingRoutes({
      targetDate,
      routeCount: routeUuids.length,
      actionUuid: undefined,
    });

    const eventChanges = { startDate: targetDate } as NewPatchRouteDto;

    try {
      const result = await store.model.mutateUpdateRoutesConstraints({
        projectId: selectedWorkspaceId,
        routeUuids,
        eventChanges,
      }, (b) => b.actionUuid.actionName).currentPromise();

      // Reconcile the resolved action UUID onto the still-pending intent so the handler claims only
      // THIS action's terminal event. UpdateRouteConstraints is reused by other flows (e.g. RouteForm
      // edits), so the UUID prevents an unrelated edit from stealing the move toast. If the terminal
      // event already arrived during the pending window, the handler has cleared the intent — don't
      // resurrect it.
      const pending = store.ui.isMovingRoutes;
      if (pending && !pending.actionUuid) {
        store.ui.setIsMovingRoutes({
          targetDate,
          routeCount: routeUuids.length,
          actionUuid: result?.updateRoutesConstraints?.actionUuid,
        });
      }

      // Only clear the bulk selection once the move has actually succeeded, so a failed
      // move leaves the user's selection intact to retry.
      if (store.ui.selectedRoutesBulk.size > 0) {
        store.ui.clearSelectedRoutesBulk();
      }

      setIsSubmitting(false);
    } catch (err: any) {
      console.error('Failed to move routes:', err);
      setIsSubmitting(false);

      // The mutation never created an action, so the subscription won't emit a terminal status.
      // Clear the pending intent we set above and surface the failure here instead.
      store.ui.setIsMovingRoutes(null);
      const appEnv = getAppEnv(store.model);
      const responseErrors = err?.response?.errors;
      const errorMessage = responseErrors?.[0]?.message || err?.message;
      appEnv.notification(moveRouteFailureNotification(selectedWorkspaceId, errorMessage, routeUuids.length));

      // Re-throw so the caller keeps the modal open instead of treating a failed move as success.
      throw err;
    }
  };

  return {
    isSubmitting,
    moveRoutes,
  };
};

export default useMoveRoutes;
